import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { WheelTimePicker } from "@/components/ui/wheel-time-picker";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { getEncargoProducts } from "@/constants/products";
import { LOCATIONS } from "@/types/orders";
import { getTimeSlotsForDate, validateTimeForDate } from "@/constants/businessHours";
import {
  Search,
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  CreditCard,
  Banknote,
  Calendar,
  Clock,
  User,
  Phone,
  Mail,
  MapPin,
  Package,
  Printer,
  Wallet,
  TimerIcon,
  CheckCircle,
} from "lucide-react";
import type { ProductDefinition } from "@/types/orders";
import { generatePickupReceiptHTML, generateTicketHTML, printViaIframe } from "@/lib/printTicket";

interface CartItem {
  product: ProductDefinition;
  quantity: number;
  notes?: string;
}

interface ManualEncargoModalProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: string;
  currentStaffName: string;
  onOrderCreated: () => void;
  token: string;
}

type PaymentOption = "efectivo" | "tarjeta";
type PaymentTiming = "now" | "later"; // "Cobrar Ahora" vs "Pagar al Recoger"
type Step = "form" | "payment-choice" | "payment-method";

export default function ManualEncargoModal({
  isOpen,
  onClose,
  locationId,
  currentStaffName,
  onOrderCreated,
  token,
}: ManualEncargoModalProps) {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [cart, setCart] = useState<CartItem[]>([]);
  const [paymentOption, setPaymentOption] = useState<PaymentOption>("efectivo");
  const [paymentTiming, setPaymentTiming] = useState<PaymentTiming>("now");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState<Step>("form");

  // Customer data
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [observations, setObservations] = useState("");

  // Pickup logistics
  const [pickupDate, setPickupDate] = useState("");
  const [pickupDateError, setPickupDateError] = useState("");
  const [pickupTime, setPickupTime] = useState("");
  const [pickupTimeError, setPickupTimeError] = useState("");
  const [pickupLocation, setPickupLocation] = useState(locationId || "siete-palmas");

  // Success state for printing
  const [createdOrder, setCreatedOrder] = useState<{
    id: string;
    order_number: number;
    loyalty_qr_token?: string;
    loyalty_short_code?: string;
    payment_status?: string;
    pickup_location?: string;
  } | null>(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  // Get encargo products
  const encargoProducts = useMemo(() => getEncargoProducts(), []);

  // Calculate minimum date (7 days from today)
  const minPickupDate = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date.toISOString().split("T")[0];
  }, []);

  // Validate pickup date
  const handlePickupDateChange = (value: string) => {
    setPickupDate(value);
    
    if (value) {
      const selectedDate = new Date(value);
      const minDate = new Date(minPickupDate);
      
      if (selectedDate < minDate) {
        setPickupDateError("Los encargos requieren 7 días de antelación");
      } else {
        setPickupDateError("");
      }
    } else {
      setPickupDateError("");
    }
  };

  // Get available time slots based on selected date
  const availableTimeSlots = useMemo(() => {
    return getTimeSlotsForDate(pickupDate || null);
  }, [pickupDate]);

  // Validate pickup time against business hours for the selected date
  const handlePickupTimeChange = (value: string) => {
    setPickupTime(value);
    
    const error = validateTimeForDate(value, pickupDate || null);
    setPickupTimeError(error);
  };

  // Re-validate time when date changes (in case Sunday/weekday changes)
  useEffect(() => {
    if (pickupTime && pickupDate) {
      const error = validateTimeForDate(pickupTime, pickupDate);
      setPickupTimeError(error);
      
      // If the time is no longer valid for this date, clear it
      const validSlots = getTimeSlotsForDate(pickupDate);
      if (!validSlots.includes(pickupTime)) {
        setPickupTime("");
        setPickupTimeError("");
      }
    }
  }, [pickupDate]);

  // Filter products by search
  const filteredProducts = useMemo(() => {
    if (!searchQuery) return encargoProducts;
    const query = searchQuery.toLowerCase();
    return encargoProducts.filter(
      (product) =>
        product.name.toLowerCase().includes(query) ||
        product.subcategory?.toLowerCase().includes(query)
    );
  }, [searchQuery, encargoProducts]);

  // Group products by subcategory
  const productsByCategory = useMemo(() => {
    const grouped: Record<string, ProductDefinition[]> = {};
    filteredProducts.forEach((product) => {
      const key = product.subcategory || "Otros";
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(product);
    });
    return grouped;
  }, [filteredProducts]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCart([]);
      setSearchQuery("");
      setCustomerName("");
      setCustomerPhone("");
      setCustomerEmail("");
      setObservations("");
      setPickupDate("");
      setPickupDateError("");
      setPickupTime("");
      setPickupTimeError("");
      setPickupLocation(locationId || "siete-palmas");
      setPaymentOption("efectivo");
      setPaymentTiming("now");
      setCurrentStep("form");
      setCreatedOrder(null);
      setShowSuccessDialog(false);
    }
  }, [isOpen, locationId]);

  // Cart calculations
  const cartTotal = useMemo(() => {
    return cart.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  }, [cart]);

  const cartItemCount = useMemo(() => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  }, [cart]);

  // Cart operations
  const addToCart = (product: ProductDefinition) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.product.id === product.id);
      if (existing) {
        return prev.map((item) =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { product, quantity: 1 }];
    });
  };

  const removeFromCart = (productId: string) => {
    setCart((prev) => prev.filter((item) => item.product.id !== productId));
  };

  const updateQuantity = (productId: string, delta: number) => {
    setCart((prev) => {
      return prev
        .map((item) => {
          if (item.product.id === productId) {
            const newQuantity = item.quantity + delta;
            return newQuantity <= 0 ? null : { ...item, quantity: newQuantity };
          }
          return item;
        })
        .filter((item): item is CartItem => item !== null);
    });
  };

  const updateItemNotes = (productId: string, notes: string) => {
    setCart((prev) =>
      prev.map((item) =>
        item.product.id === productId ? { ...item, notes } : item
      )
    );
  };

  // Validation
  const isFormValid = useMemo(() => {
    const hasValidDate = pickupDate !== "" && pickupDateError === "";
    const hasValidTime = pickupTime !== "" && pickupTimeError === "";
    return (
      cart.length > 0 &&
      customerName.trim() !== "" &&
      hasValidDate &&
      hasValidTime &&
      pickupLocation !== ""
    );
  }, [cart, customerName, pickupDate, pickupDateError, pickupTime, pickupTimeError, pickupLocation]);

  // Get location name
  const getLocationName = (locId: string) => {
    const loc = LOCATIONS.find((l) => l.id === locId);
    return loc?.name || locId;
  };

  // Handle print pickup receipt (for "Pagar al Recoger" option)
  const handlePrintPickupReceipt = () => {
    if (!createdOrder) return;

    const orderData = {
      order_number: createdOrder.order_number,
      customer_name: customerName,
      customer_phone: customerPhone,
      pickup_date: pickupDate,
      pickup_time: pickupTime,
      pickup_location: createdOrder.pickup_location || getLocationName(pickupLocation),
      items: cart.map((item) => ({
        product_name: item.product.name,
        quantity: item.quantity,
        notes: item.notes,
      })),
      observations,
      total: cartTotal,
      payment_status: createdOrder.payment_status as 'pendiente' | 'pagado' | 'pago_en_tienda',
    };

    const html = generatePickupReceiptHTML(orderData);
    printViaIframe(html);
  };

  // Handle print full ticket with loyalty QR (for "Cobrar Ahora" option)
  const handlePrintFullTicket = () => {
    if (!createdOrder) return;

    // Build a full order object for ticket generation
    const orderForTicket = {
      id: createdOrder.id,
      order_number: createdOrder.order_number,
      order_type: "encargo" as const,
      status: "pendiente" as const,
      payment_method: paymentOption === "efectivo" ? "local" as const : "local" as const,
      payment_status: "pagado" as const,
      location_id: pickupLocation,
      customer_name: customerName,
      customer_phone: customerPhone || null,
      customer_email: customerEmail || null,
      pickup_date: pickupDate,
      pickup_time: pickupTime,
      pickup_location: createdOrder.pickup_location || getLocationName(pickupLocation),
      observations: observations || null,
      served_by: currentStaffName,
      subtotal: cartTotal,
      total: cartTotal,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      loyalty_qr_token: createdOrder.loyalty_qr_token,
      loyalty_short_code: createdOrder.loyalty_short_code,
      items: cart.map((item) => ({
        id: crypto.randomUUID(),
        order_id: createdOrder.id,
        product_name: item.product.name + (item.notes ? ` (${item.notes})` : ""),
        product_category: item.product.category,
        unit_price: item.product.price,
        quantity: item.quantity,
        subtotal: item.product.price * item.quantity,
        created_at: new Date().toISOString(),
      })),
      locations: {
        id: pickupLocation,
        code: pickupLocation,
        name: createdOrder.pickup_location || getLocationName(pickupLocation),
      },
    };

    const html = generateTicketHTML(orderForTicket as any, "ticket");
    printViaIframe(html);
  };

  // Proceed to payment choice step
  const handleProceedToPayment = () => {
    if (!isFormValid) {
      toast({
        title: "Formulario incompleto",
        description: "Por favor, completa todos los campos obligatorios",
        variant: "destructive",
      });
      return;
    }
    setCurrentStep("payment-choice");
  };

  // Select payment timing and proceed
  const handleSelectPaymentTiming = (timing: PaymentTiming) => {
    setPaymentTiming(timing);
    if (timing === "now") {
      setCurrentStep("payment-method");
    } else {
      // "Pagar al Recoger" - submit directly with pending status
      handleSubmitOrder("pendiente");
    }
  };

  // Final submission with payment method
  const handleFinalSubmit = () => {
    handleSubmitOrder("pagado");
  };

  // Submit order to backend
  const handleSubmitOrder = async (paymentStatus: "pagado" | "pendiente") => {
    setIsSubmitting(true);

    try {
      // Call Edge Function to create encargo order
      const { data, error } = await supabase.functions.invoke("admin-orders", {
        body: {
          action: "create-encargo",
          token,
          orderData: {
            order_type: "encargo",
            status: "pendiente",
            payment_status: paymentStatus,
            location_id: pickupLocation,
            customer_name: customerName.trim(),
            customer_phone: customerPhone.trim() || null,
            customer_email: customerEmail.trim() || null,
            pickup_date: pickupDate,
            pickup_time: pickupTime,
            pickup_location: getLocationName(pickupLocation),
            observations: observations.trim() || null,
            served_by: currentStaffName,
            payment_method: paymentOption === "efectivo" ? "local" : "local",
          },
          items: cart.map((item) => ({
            product_name: item.product.name + (item.notes ? ` (${item.notes})` : ""),
            product_category: item.product.category,
            unit_price: item.product.price,
            quantity: item.quantity,
          })),
          sendNotifications: paymentStatus === "pagado" && !!(customerPhone || customerEmail),
        },
      });

      if (error) throw error;

      if (!data.success) {
        throw new Error(data.error || "Error al crear encargo");
      }

      const paymentLabel = paymentStatus === "pagado" ? "Cobrado" : "Pendiente de pago";
      toast({
        title: `✅ Encargo #${data.order.order_number} creado`,
        description: `Recogida: ${pickupDate} a las ${pickupTime} • ${paymentLabel}`,
      });

      // Store created order for printing
      setCreatedOrder(data.order);
      setShowSuccessDialog(true);

    } catch (error: any) {
      console.error("Error creating encargo:", error);
      toast({
        title: "Error al crear encargo",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle close success dialog
  const handleCloseSuccess = () => {
    setShowSuccessDialog(false);
    onOrderCreated();
    onClose();
  };

  // Back to previous step
  const handleBack = () => {
    if (currentStep === "payment-method") {
      setCurrentStep("payment-choice");
    } else if (currentStep === "payment-choice") {
      setCurrentStep("form");
    }
  };

  // Payment Choice Dialog (Step 2)
  if (currentStep === "payment-choice" && !showSuccessDialog) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Wallet className="h-6 w-6 text-purple-600" />
              ¿Cuándo se cobra?
            </DialogTitle>
          </DialogHeader>

          <div className="py-6 space-y-4">
            {/* Summary */}
            <div className="bg-muted/50 p-4 rounded-lg space-y-2 text-sm mb-6">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Cliente:</span>
                <span className="font-medium">{customerName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Recogida:</span>
                <span className="font-medium">{pickupDate} a las {pickupTime}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Sede:</span>
                <span className="font-medium">{getLocationName(pickupLocation)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="font-medium">Total:</span>
                <span className="text-xl font-bold text-primary">{cartTotal.toFixed(2)}€</span>
              </div>
            </div>

            {/* Payment Timing Options */}
            <div className="grid grid-cols-1 gap-4">
              {/* Option A: Cobrar Ahora */}
              <button
                onClick={() => handleSelectPaymentTiming("now")}
                disabled={isSubmitting}
                className="p-6 border-2 rounded-xl text-left hover:border-green-500 hover:bg-green-50 transition-all group"
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-100 rounded-full group-hover:bg-green-200 transition-colors">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-green-700">💸 Cobrar Ahora</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      El cliente paga ahora. Se imprimirá el ticket completo con QR de puntos.
                    </p>
                    <div className="mt-2 flex gap-2">
                      <Badge variant="secondary" className="text-xs">
                        <Banknote className="h-3 w-3 mr-1" />
                        Efectivo
                      </Badge>
                      <Badge variant="secondary" className="text-xs">
                        <CreditCard className="h-3 w-3 mr-1" />
                        Tarjeta
                      </Badge>
                    </div>
                  </div>
                </div>
              </button>

              {/* Option B: Pagar al Recoger */}
              <button
                onClick={() => handleSelectPaymentTiming("later")}
                disabled={isSubmitting}
                className="p-6 border-2 rounded-xl text-left hover:border-orange-500 hover:bg-orange-50 transition-all group"
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-orange-100 rounded-full group-hover:bg-orange-200 transition-colors">
                    <TimerIcon className="h-8 w-8 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-orange-700">⏳ Pagar al Recoger</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Se crea el encargo sin cobrar. Aparecerá con etiqueta "PENDIENTE DE PAGO".
                    </p>
                    <Badge variant="outline" className="mt-2 text-xs border-orange-400 text-orange-600">
                      Se cobrará cuando el cliente recoja
                    </Badge>
                  </div>
                </div>
              </button>
            </div>

            {isSubmitting && (
              <div className="text-center py-4">
                <span className="animate-pulse text-muted-foreground">Creando encargo...</span>
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <Button variant="outline" onClick={handleBack} disabled={isSubmitting}>
              ← Volver
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Payment Method Dialog (Step 3 - only for "Cobrar Ahora")
  if (currentStep === "payment-method" && !showSuccessDialog) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Wallet className="h-6 w-6 text-green-600" />
              Método de Pago
            </DialogTitle>
          </DialogHeader>

          <div className="py-6 space-y-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-primary">{cartTotal.toFixed(2)}€</p>
              <p className="text-muted-foreground mt-1">Total a cobrar</p>
            </div>

            <RadioGroup
              value={paymentOption}
              onValueChange={(v) => setPaymentOption(v as PaymentOption)}
              className="space-y-4"
            >
              <label
                htmlFor="pay-efectivo"
                className={`flex items-center gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                  paymentOption === "efectivo" ? "border-green-500 bg-green-50" : "hover:border-muted-foreground"
                }`}
              >
                <RadioGroupItem value="efectivo" id="pay-efectivo" />
                <Banknote className="h-8 w-8 text-green-600" />
                <div>
                  <p className="font-bold">Efectivo</p>
                  <p className="text-sm text-muted-foreground">Pago en metálico</p>
                </div>
              </label>

              <label
                htmlFor="pay-tarjeta"
                className={`flex items-center gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                  paymentOption === "tarjeta" ? "border-blue-500 bg-blue-50" : "hover:border-muted-foreground"
                }`}
              >
                <RadioGroupItem value="tarjeta" id="pay-tarjeta" />
                <CreditCard className="h-8 w-8 text-blue-600" />
                <div>
                  <p className="font-bold">Tarjeta (Datáfono)</p>
                  <p className="text-sm text-muted-foreground">Cobrar con TPV local</p>
                </div>
              </label>
            </RadioGroup>
          </div>

          <div className="flex gap-3">
            <Button variant="outline" onClick={handleBack} disabled={isSubmitting} className="flex-1">
              ← Volver
            </Button>
            <Button
              onClick={handleFinalSubmit}
              disabled={isSubmitting}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              {isSubmitting ? (
                <span className="animate-pulse">Procesando...</span>
              ) : (
                <>
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Cobrar {cartTotal.toFixed(2)}€
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Success Dialog
  if (showSuccessDialog && createdOrder) {
    const isPaid = createdOrder.payment_status === "pagado";
    
    return (
      <Dialog open={true} onOpenChange={() => handleCloseSuccess()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 text-xl ${isPaid ? "text-green-600" : "text-orange-600"}`}>
              <Package className="h-6 w-6" />
              {isPaid ? "✅ Encargo Cobrado" : "📦 Encargo Reservado"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-primary">
                #{createdOrder.order_number}
              </p>
              <p className="text-muted-foreground mt-1">Número de Encargo</p>
            </div>

            {/* Payment Status Badge */}
            <div className="flex justify-center">
              {isPaid ? (
                <Badge className="bg-green-100 text-green-700 border-green-300 text-sm px-4 py-2">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  PAGADO
                </Badge>
              ) : (
                <Badge className="bg-orange-100 text-orange-700 border-orange-300 text-sm px-4 py-2">
                  <TimerIcon className="h-4 w-4 mr-2" />
                  PENDIENTE DE PAGO
                </Badge>
              )}
            </div>

            <div className="bg-muted/50 p-4 rounded-lg space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Cliente:</span>
                <span className="font-medium">{customerName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Recogida:</span>
                <span className="font-medium">
                  {pickupDate} a las {pickupTime}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Sede:</span>
                <span className="font-medium">{createdOrder.pickup_location || getLocationName(pickupLocation)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total:</span>
                <span className="font-bold text-primary">{cartTotal.toFixed(2)}€</span>
              </div>
            </div>

            {/* Loyalty info for paid orders */}
            {isPaid && createdOrder.loyalty_short_code && (
              <div className="bg-purple-50 border border-purple-200 p-3 rounded-lg text-center">
                <p className="text-xs text-purple-600 mb-1">Código de Puntos</p>
                <p className="font-mono font-bold text-lg text-purple-700">{createdOrder.loyalty_short_code}</p>
              </div>
            )}

            {(customerPhone || customerEmail) && isPaid && (
              <p className="text-xs text-center text-muted-foreground">
                📧 Se enviarán confirmación y recordatorio automáticos
              </p>
            )}

            {!isPaid && (
              <p className="text-xs text-center text-orange-600 font-medium">
                ⚠️ Recordar cobrar al cliente cuando recoja el pedido
              </p>
            )}
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleCloseSuccess}
            >
              Cerrar
            </Button>
            <Button
              className={`flex-1 ${isPaid ? "bg-purple-600 hover:bg-purple-700" : "bg-orange-600 hover:bg-orange-700"}`}
              onClick={isPaid ? handlePrintFullTicket : handlePrintPickupReceipt}
            >
              <Printer className="h-4 w-4 mr-2" />
              {isPaid ? "Imprimir Ticket" : "Imprimir Comprobante"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Main Form (Step 1)
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-6xl h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Package className="h-6 w-6 text-purple-600" />
            Crear Encargo Manual
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 overflow-hidden">
          {/* Left Column - Product Catalog */}
          <div className="flex-1 flex flex-col border-r">
            {/* Search */}
            <div className="p-4 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar tartas y encargos..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Products Grid */}
            <ScrollArea className="flex-1">
              <div className="p-4 space-y-6">
                {Object.entries(productsByCategory).map(([category, products]) => (
                  <div key={category}>
                    <h3 className="font-semibold text-sm text-muted-foreground mb-3">
                      {category}
                    </h3>
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                      {products.map((product) => (
                        <button
                          key={product.id}
                          onClick={() => addToCart(product)}
                          className="p-3 text-left rounded-lg border bg-card hover:bg-accent transition-colors"
                        >
                          <p className="font-medium text-sm line-clamp-2">
                            {product.name}
                          </p>
                          {product.description && (
                            <p className="text-xs text-muted-foreground line-clamp-1 mt-1">
                              {product.description}
                            </p>
                          )}
                          <p className="text-primary font-bold mt-1">
                            {product.price > 0 ? `${product.price.toFixed(2)}€` : "Consultar"}
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}

                {filteredProducts.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">
                    No se encontraron productos
                  </p>
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Right Column - Order Details */}
          <div className="w-[450px] flex flex-col bg-muted/30">
            <ScrollArea className="flex-1">
              <div className="p-4 space-y-4">
                {/* Customer Data */}
                <Card>
                  <CardContent className="p-4 space-y-3">
                    <h4 className="font-semibold flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Datos del Cliente
                    </h4>

                    <div>
                      <Label className="text-xs text-muted-foreground">
                        Nombre Completo *
                      </Label>
                      <Input
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        placeholder="Ej: María García López"
                        className="mt-1"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-muted-foreground flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          Teléfono
                        </Label>
                        <Input
                          value={customerPhone}
                          onChange={(e) => setCustomerPhone(e.target.value)}
                          placeholder="928123456"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          Email
                        </Label>
                        <Input
                          type="email"
                          value={customerEmail}
                          onChange={(e) => setCustomerEmail(e.target.value)}
                          placeholder="cliente@email.com"
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Pickup Logistics */}
                <Card>
                  <CardContent className="p-4 space-y-3">
                    <h4 className="font-semibold flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Datos de Recogida
                    </h4>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-muted-foreground flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Fecha * (mín. 7 días)
                        </Label>
                        <Input
                          type="date"
                          value={pickupDate}
                          onChange={(e) => handlePickupDateChange(e.target.value)}
                          min={minPickupDate}
                          className={`mt-1 ${pickupDateError ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                        />
                        {pickupDateError && (
                          <p className="text-xs text-red-500 mt-1">{pickupDateError}</p>
                        )}
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Hora * {pickupDate && new Date(pickupDate).getDay() === 0 
                            ? "(Domingos: 09:00 - 14:00)" 
                            : "(09:00-14:00, 16:30-20:30)"}
                        </Label>
                        <div className="mt-1">
                          <WheelTimePicker
                            value={pickupTime}
                            onChange={handlePickupTimeChange}
                            timeSlots={availableTimeSlots}
                            placeholder="Seleccionar hora"
                            error={!!pickupTimeError}
                          />
                        </div>
                        {pickupTimeError && (
                          <p className="text-xs text-red-500 mt-1">{pickupTimeError}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs text-muted-foreground">
                        Sede de Recogida *
                      </Label>
                      <RadioGroup
                        value={pickupLocation}
                        onValueChange={setPickupLocation}
                        className="flex gap-4 mt-2"
                      >
                        {LOCATIONS.map((loc) => (
                          <div key={loc.id} className="flex items-center space-x-2">
                            <RadioGroupItem value={loc.id} id={`loc-${loc.id}`} />
                            <Label
                              htmlFor={`loc-${loc.id}`}
                              className="cursor-pointer text-sm"
                            >
                              {loc.name}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>
                  </CardContent>
                </Card>

                {/* Cart Items */}
                <Card>
                  <CardContent className="p-4">
                    <h4 className="font-semibold flex items-center gap-2 mb-3">
                      <ShoppingCart className="h-4 w-4" />
                      Productos ({cartItemCount})
                    </h4>

                    {cart.length === 0 ? (
                      <div className="text-center text-muted-foreground py-4">
                        <ShoppingCart className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Selecciona productos</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {cart.map((item) => (
                          <div
                            key={item.product.id}
                            className="p-3 rounded-lg border bg-background"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">
                                  {item.product.name}
                                </p>
                                <p className="text-muted-foreground text-xs">
                                  {item.product.price > 0
                                    ? `${item.product.price.toFixed(2)}€ × ${item.quantity}`
                                    : "Consultar precio"}
                                </p>
                              </div>
                              {item.product.price > 0 && (
                                <p className="font-bold text-primary">
                                  {(item.product.price * item.quantity).toFixed(2)}€
                                </p>
                              )}
                            </div>

                            {/* Notes input */}
                            <Textarea
                              placeholder="Notas (ej: 'Felicidades Ana')"
                              value={item.notes || ""}
                              onChange={(e) =>
                                updateItemNotes(item.product.id, e.target.value)
                              }
                              className="mt-2 h-12 text-xs"
                            />

                            {/* Quantity controls */}
                            <div className="flex items-center justify-between mt-2">
                              <div className="flex items-center gap-2">
                                <Button
                                  size="icon"
                                  variant="outline"
                                  className="h-7 w-7"
                                  onClick={() => updateQuantity(item.product.id, -1)}
                                >
                                  <Minus className="h-3 w-3" />
                                </Button>
                                <span className="font-medium text-sm w-6 text-center">
                                  {item.quantity}
                                </span>
                                <Button
                                  size="icon"
                                  variant="outline"
                                  className="h-7 w-7"
                                  onClick={() => updateQuantity(item.product.id, 1)}
                                >
                                  <Plus className="h-3 w-3" />
                                </Button>
                              </div>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7 text-destructive hover:text-destructive"
                                onClick={() => removeFromCart(item.product.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Observations */}
                <Card>
                  <CardContent className="p-4">
                    <Label className="text-xs text-muted-foreground">
                      Observaciones Generales
                    </Label>
                    <Textarea
                      value={observations}
                      onChange={(e) => setObservations(e.target.value)}
                      placeholder="Notas adicionales para el pedido..."
                      className="mt-1 h-20"
                    />
                  </CardContent>
                </Card>
              </div>
            </ScrollArea>

            {/* Total & Submit */}
            <div className="p-4 border-t bg-primary/5">
              <div className="flex items-center justify-between mb-4">
                <span className="font-medium">Total</span>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{cartItemCount} items</Badge>
                  <span className="text-2xl font-bold text-primary">
                    {cartTotal > 0 ? `${cartTotal.toFixed(2)}€` : "Consultar"}
                  </span>
                </div>
              </div>
              <Button
                className="w-full h-12 text-lg font-bold bg-purple-600 hover:bg-purple-700"
                onClick={handleProceedToPayment}
                disabled={!isFormValid}
              >
                <span className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Continuar
                </span>
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
